// Preis-Hintergrundlauf-Fortschritt (08/2026): Nach dem Speichern individueller
// Preise werden alle Termine + offenen Rechnungen im Hintergrund neu berechnet.
// Diese Karte pollt /customers/{id}/reprice-status und zeigt X/Y Termine.
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { X, Tag, CheckCircle2, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RepriceProgress = ({ customerId, jobId, customerName, onDone }) => {
  const [status, setStatus] = useState(null);
  const [visible, setVisible] = useState(true);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!customerId || !jobId) return;
    setVisible(true);
    setStatus(null);
    let cancelled = false;
    const token = localStorage.getItem('token');
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/customers/${customerId}/reprice-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        setStatus(res.data);
        const st = res.data?.status;
        if (st === 'running') {
          timerRef.current = setTimeout(poll, 1500);
        } else {
          // done oder error → nach kurzer Anzeige ausblenden
          timerRef.current = setTimeout(() => {
            setVisible(false);
            onDone && onDone();
          }, 6000);
        }
      } catch (e) {
        if (!cancelled) timerRef.current = setTimeout(poll, 3000);
      }
    };
    poll();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [customerId, jobId, onDone]);

  if (!customerId || !jobId || !visible || !status) return null;

  const st = status.status;
  const total = status.total_appointments || 0;
  const processed = status.processed_appointments || 0;
  const updated = status.updated_appointments || 0;
  const invoices = status.invoices_recalculated || 0;
  const isError = st === 'error';
  const finished = st === 'done';
  const pct = finished ? 100 : (total > 0 ? Math.min(99, Math.round((processed / total) * 100)) : (isError ? 0 : 5));

  const barColor = isError ? 'bg-red-500' : finished ? 'bg-emerald-500' : 'bg-indigo-600';
  const Icon = isError ? AlertTriangle : finished ? CheckCircle2 : Tag;
  const iconColor = isError ? 'text-red-600' : finished ? 'text-emerald-600' : 'text-indigo-600';

  let title;
  if (isError) title = 'Preis-Aktualisierung fehlgeschlagen';
  else if (finished) title = 'Preise vollständig aktualisiert ✅';
  else title = 'Preise werden aktualisiert…';

  return (
    <div
      data-testid="reprice-progress"
      className="fixed bottom-6 left-6 z-50 w-[340px] bg-white rounded-xl shadow-2xl border border-gray-200 p-4"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${iconColor}`} />
          <span className="text-sm font-semibold text-gray-800" data-testid="reprice-progress-title">
            {title}
          </span>
        </div>
        <button
          data-testid="reprice-progress-close"
          onClick={() => { setVisible(false); onDone && onDone(); }}
          className="p-0.5 rounded hover:bg-gray-100 text-gray-400"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {customerName && (
        <p className="mt-1 text-xs text-gray-500">Kunde: {customerName}</p>
      )}

      <div className="mt-3 h-2.5 w-full bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${barColor}`}
          style={{ width: `${pct}%` }}
        />
      </div>

      <p className="mt-1.5 text-xs text-gray-600" data-testid="reprice-progress-count">
        {isError ? (
          <span className="text-red-600">{status.error || 'Bitte erneut versuchen.'}</span>
        ) : finished ? (
          <>{updated} Termine angepasst · {invoices} offene Rechnung(en) neu berechnet</>
        ) : (
          <>{processed}/{total} Termine geprüft ({pct}%) — du kannst normal weiterarbeiten</>
        )}
      </p>
    </div>
  );
};

export default RepriceProgress;
